import { LinkOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Result, message } from 'antd';
import { FC, useRef, useState } from 'react';

import {
  AccessDenied,
  AudienceToggle,
  CampaignStats,
  CoverageExceptions,
  EMPTY_EXCLUSIONS,
  RecipientTree,
  SyncConfirmModal,
  SyncResult,
  audienceHint,
  audienceLabel,
  computeCoverage,
  uniqueRecipientCount,
} from '@entities';
import type { Exclusions, RecipientTreeHandle } from '@entities';
import { useAccount } from '@hooks';
import { DEFAULT_AUDIENCE, useNewsletter, useNewsletterStats } from '@hooks-api';
import type { NewsletterAudience } from '@hooks-api';
import { getApiErrorMessage } from '@utils';
import { env } from '@utils/env';

import * as S from './styled';

const Newsletter: FC = () => {
  const { role } = useAccount();
  const [audience, setAudience] = useState<NewsletterAudience>(DEFAULT_AUDIENCE);
  const {
    companies,
    isLoading,
    isError,
    refetch,
    sync,
    isSyncing,
    syncCountdown,
    syncSummary,
    syncFailed,
    syncTimedOut,
    dismissResult,
  } = useNewsletter(audience);
  const {
    campaigns,
    selectedId,
    selectCampaign,
    isLoadingCampaigns,
    campaignsError,
    refetchCampaigns,
    stats,
    isLoadingStats,
    statsError,
  } = useNewsletterStats();

  const [exclusions, setExclusions] = useState<Exclusions>(EMPTY_EXCLUSIONS);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const treeRef = useRef<RecipientTreeHandle>(null);

  // Defense in depth: the router guards the route's roles, but re-check here.
  if (role !== 'admin') return <AccessDenied />;

  const coverage = computeCoverage(companies, exclusions);
  const recipientCount = uniqueRecipientCount(companies, exclusions);
  const includedCompanies = companies.length - coverage.excluded.length;
  const listmonkUrl = env.VITE_LISTMONK_URL;
  // While a job is running, count down from the 30s ceiling so the button shows
  // progress and the user knows it hasn't stalled.
  const syncLabel = isSyncing
    ? syncCountdown != null
      ? `Syncing… ${syncCountdown}s`
      : 'Syncing…'
    : 'Sync clients';

  /**
   * Switching audiences replaces the recipient list, so the selection can't
   * carry over: ids from the old pool would silently exclude (or fail to
   * exclude) the wrong people. Reset to "everyone in the new audience", drop the
   * previous sync result (it described the old pool), and remount the tree so
   * its search and expansion start clean too.
   */
  const handleAudienceChange = (next: NewsletterAudience) => {
    if (next === audience) return;
    setAudience(next);
    setExclusions(EMPTY_EXCLUSIONS);
    dismissResult();
  };

  const handleConfirm = async () => {
    try {
      const { total } = await sync({
        excludedCompanyIds: exclusions.companyIds,
        excludedUserIds: exclusions.userIds,
      });
      setConfirmOpen(false);
      message.success(`Sync started for ${total} recipient${total === 1 ? '' : 's'}`);
    } catch (error) {
      setConfirmOpen(false);
      message.error(getApiErrorMessage(error, 'Failed to start sync'));
    }
  };

  return (
    <S.Wrapper vertical gap={24}>
      <S.Header align="flex-start" justify="space-between" gap={16}>
        <Flex vertical gap={2}>
          <S.Title>Newsletter</S.Title>
          <S.Subtitle>
            Choose who receives the monthly Product updates email, then sync the list to Listmonk.
            Composing and sending happen in Listmonk.
          </S.Subtitle>
        </Flex>
        <Flex gap={12}>
          {listmonkUrl && (
            <Button icon={<LinkOutlined />} href={listmonkUrl} target="_blank" rel="noreferrer">
              Campaigns
            </Button>
          )}
          <Button
            type="primary"
            icon={<SyncOutlined />}
            loading={isSyncing}
            disabled={recipientCount === 0}
            onClick={() => setConfirmOpen(true)}
          >
            {syncLabel}
          </Button>
        </Flex>
      </S.Header>

      <S.Grid>
        <S.RecipientCard
          title="Recipients"
          extra={
            <AudienceToggle value={audience} onChange={handleAudienceChange} disabled={isSyncing} />
          }
        >
          <S.AudienceHint>
            {audienceHint(audience)} Members whose email address isn’t confirmed are never listed.
          </S.AudienceHint>
          {isError ? (
            <Result
              status="warning"
              title="Couldn't load recipients"
              subTitle="The recipient list failed to load. If your session lost admin access this will keep failing; otherwise try again."
              extra={
                <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                  Try again
                </Button>
              }
            />
          ) : (
            <>
              <RecipientTree
                // Remount on an audience switch so the tree's own search and
                // expansion reset with the list.
                key={audience}
                ref={treeRef}
                companies={companies}
                exclusions={exclusions}
                onChange={setExclusions}
                isLoading={isLoading}
              />
              {!isLoading && (
                <>
                  <S.RecipientDivider />
                  <CoverageExceptions
                    coverage={coverage}
                    onSelectCompany={(companyId) => treeRef.current?.focusCompany(companyId)}
                  />
                </>
              )}
            </>
          )}
        </S.RecipientCard>

        <S.Side vertical gap={16}>
          <Card>
            <S.SumRow align="center" justify="space-between">
              <S.SumLabel>Companies</S.SumLabel>
              <S.SumValue>
                {includedCompanies}
                <S.SumValueMuted> / {companies.length}</S.SumValueMuted>
              </S.SumValue>
            </S.SumRow>
            <S.SumRow align="center" justify="space-between">
              <S.SumLabel>Unique recipients</S.SumLabel>
              <S.SumValue accent>{recipientCount}</S.SumValue>
            </S.SumRow>
            <S.Hint>One email = one recipient. People in several companies are counted once.</S.Hint>
          </Card>

          <SyncResult
            summary={syncSummary}
            failed={syncFailed}
            timedOut={syncTimedOut}
            onDismiss={dismissResult}
          />
        </S.Side>
      </S.Grid>

      <CampaignStats
        campaigns={campaigns}
        selectedId={selectedId}
        onSelect={selectCampaign}
        isLoadingCampaigns={isLoadingCampaigns}
        campaignsError={campaignsError}
        onRetryCampaigns={refetchCampaigns}
        stats={stats}
        isLoadingStats={isLoadingStats}
        statsError={statsError}
      />

      <SyncConfirmModal
        open={confirmOpen}
        recipientCount={recipientCount}
        audience={audienceLabel(audience)}
        confirmLoading={isSyncing}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </S.Wrapper>
  );
};

export default Newsletter;
